/**
 * PeCash Design System
 * 
 * ONE consistent design system across:
 * - Customer
 * - Beneficiary  
 * - Merchant
 * - Admin
 * 
 * STYLE:
 * - Premium fintech
 * - Clean surfaces
 * - Generous whitespace
 * - Rounded cards
 * - Clear typography
 * - Large monetary amounts
 * - Subtle shadows
 * - Smooth transitions
 * - Professional icons
 * - Accessible contrast
 * - Large mobile touch targets
 */

// Brand Colors
export const colors = {
  // Primary - PeCash Purple
  primary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',  // Main brand color
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  
  // Success/Approved - Green
  success: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',  // Approved/Completed
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  
  // Warning/Pending - Amber
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',  // Pending/Waiting
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  
  // Error/Declined - Red
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',  // Declined/Error
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  
  // Neutral - Slate
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

// Financial State Colors (with icons/labels, not color alone)
export const stateColors = {
  approved: 'success',
  completed: 'success',
  delivered: 'success',
  pending: 'warning',
  waiting: 'warning',
  preparing: 'warning',
  declined: 'error',
  error: 'error',
  cancelled: 'error',
  expired: 'neutral',
  informational: 'neutral',
};

// Typography
export const typography = {
  // Large monetary amounts
  money: {
    large: 'text-4xl sm:text-5xl font-bold tracking-tight',
    medium: 'text-2xl sm:text-3xl font-bold',
    small: 'text-xl font-semibold',
  },
  
  // Headings
  heading: {
    h1: 'text-2xl sm:text-3xl font-bold text-slate-900',
    h2: 'text-xl sm:text-2xl font-semibold text-slate-800',
    h3: 'text-lg font-semibold text-slate-800',
  },
  
  // Body text
  body: {
    large: 'text-base sm:text-lg text-slate-600',
    normal: 'text-sm sm:text-base text-slate-600',
    small: 'text-xs sm:text-sm text-slate-500',
  },
  
  // Labels
  label: 'text-sm font-medium text-slate-700',
  caption: 'text-xs text-slate-500',
};

// Spacing
export const spacing = {
  // Card padding
  card: {
    mobile: 'p-4',
    desktop: 'p-6',
  },
  
  // Section spacing
  section: {
    mobile: 'py-6',
    desktop: 'py-8',
  },
  
  // Gap between elements
  gap: {
    tight: 'gap-2',
    normal: 'gap-4',
    loose: 'gap-6',
  },
};

// Shadows
export const shadows = {
  card: 'shadow-sm hover:shadow-md transition-shadow',
  elevated: 'shadow-lg',
  button: 'shadow-md hover:shadow-lg',
};

// Border radius
export const radius = {
  card: 'rounded-2xl',
  button: 'rounded-xl',
  input: 'rounded-lg',
  badge: 'rounded-full',
  avatar: 'rounded-full',
};

// Mobile touch targets (minimum 44px)
export const touchTargets = {
  button: 'min-h-[44px] min-w-[44px]',
  icon: 'w-10 h-10 sm:w-12 sm:h-12',
  nav: 'h-14',
};

// Animation
export const animation = {
  fadeIn: 'animate-fadeIn',
  slideUp: 'animate-slideUp',
  pulse: 'animate-pulse',
  spin: 'animate-spin',
  bounce: 'animate-bounce',
};

// Reduced motion variants for accessibility
// Use these when users prefer reduced motion
export const reducedMotion = {
  // Framer Motion variants that respect prefers-reduced-motion
  fadeInVariant: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15 }
  },
  slideUpVariant: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
    transition: { duration: 0.15 }
  },
  // CSS class for elements that should disable animation
  noMotion: 'motion-reduce:transition-none motion-reduce:animate-none',
};

// Motion-safe animation classes
export const motionSafe = {
  transition: 'motion-safe:transition-all motion-safe:duration-200',
  hover: 'motion-safe:hover:scale-[1.02]',
  active: 'motion-safe:active:scale-[0.98]',
};

// Common component classes
export const components = {
  // Primary CTA button
  buttonPrimary: `
    w-full h-14 
    bg-purple-600 hover:bg-purple-700 
    text-white font-semibold text-lg
    rounded-2xl shadow-lg shadow-purple-500/25
    transition-all duration-200
    active:scale-[0.98]
    disabled:opacity-50 disabled:cursor-not-allowed
  `,
  
  // Secondary button
  buttonSecondary: `
    w-full h-12
    bg-white hover:bg-slate-50
    text-slate-700 font-medium
    border-2 border-slate-200
    rounded-xl
    transition-all duration-200
    active:scale-[0.98]
  `,
  
  // Card
  card: `
    bg-white 
    rounded-2xl 
    border border-slate-100 
    shadow-sm hover:shadow-md
    transition-all duration-200
    overflow-hidden
  `,
  
  // Input field
  input: `
    w-full h-12
    px-4
    bg-white
    border-2 border-slate-200 
    rounded-xl
    text-slate-900 
    placeholder:text-slate-400
    focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20
    transition-all duration-200
  `,
  
  // Amount display
  amountLarge: `
    text-4xl sm:text-5xl 
    font-bold 
    tracking-tight
  `,
  
  // Badge
  badge: `
    inline-flex items-center
    px-3 py-1
    rounded-full
    text-sm font-medium
  `,
  
  // Mobile bottom navigation
  bottomNav: `
    fixed bottom-0 left-0 right-0
    h-16 safe-area-bottom
    bg-white border-t border-slate-200
    flex items-center justify-around
    px-4
  `,
  
  // Mobile bottom navigation item
  bottomNavItem: `
    flex flex-col items-center justify-center
    min-w-[64px] h-full
    text-slate-500
    transition-colors duration-200
  `,
  
  // Bottom sheet
  bottomSheet: `
    fixed inset-x-0 bottom-0
    bg-white
    rounded-t-3xl
    shadow-2xl
    p-6 pb-safe
    max-h-[90vh]
    overflow-y-auto
  `,
};

// Quick amount buttons for buy flow
export const quickAmounts = [25, 50, 75, 100, 150, 200];

// Currency formatting
export const formatUSD = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

export const formatDOP = (amount) => {
  return `RD$${new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
};

// Time formatting
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Status badges
export const getStatusBadge = (status) => {
  const config = {
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Aprobado' },
    completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completado' },
    delivered: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Entregado' },
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pendiente' },
    pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Esperando' },
    waiting: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Esperando' },
    preparing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Preparando' },
    out_for_delivery: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Camino' },
    declined: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rechazado' },
    cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Cancelado' },
    expired: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Expirado' },
  };
  return config[status] || config.pending;
};

export default {
  colors,
  stateColors,
  typography,
  spacing,
  shadows,
  radius,
  touchTargets,
  animation,
  components,
  quickAmounts,
  formatUSD,
  formatDOP,
  formatTime,
  getStatusBadge,
};
